import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, User, Hash, Calendar, CheckCircle, Heart } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, TextInput, Alert, PageHeader, Section } from '../components/UI';
import { usePatient } from '../contexts/PatientContext';
import { API_URL, API_KEY } from '../config';

// SHA-256 hash using Web Crypto API
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Build raw key: INITIALS-DOB-MOTHER_INITIALS
function buildRawId(fullName, dob, motherName) {
  const initials = fullName.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('');
  const motherInitials = motherName.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('');
  return `${initials}-${dob}-${motherInitials}`;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS   = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const YEARS  = Array.from({ length: 120 }, (_, i) => String(new Date().getFullYear() - i));

export default function PatientPage() {
  const navigate = useNavigate();
  const { setPatient } = usePatient();
  const [form, setForm] = useState({ fullName: '', motherName: '' });
  const [dob, setDob] = useState({ month: '', day: '', year: '' });
  const [rawId, setRawId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Combine dob parts → YYYY-MM-DD
  const dobString = dob.year && dob.month && dob.day
    ? `${dob.year}-${dob.month}-${dob.day}`
    : '';

  // Recompute raw label + hashed ID whenever form or dob changes
  useEffect(() => {
    const { fullName, motherName } = form;
    if (!fullName.trim() || !dobString || !motherName.trim()) {
      setRawId('');
      setPatientId('');
      return;
    }
    const raw = buildRawId(fullName, dobString, motherName);
    setRawId(raw);
    sha256(raw).then(hash => setPatientId(`PAT-${hash.slice(0, 12).toUpperCase()}`));
  }, [form, dobString]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { fullName, motherName } = form;
    if (!fullName.trim() || !dobString || !motherName.trim()) { setError('All fields are required.'); return; }
    if (!patientId) { setError('Patient ID is not ready yet. Please wait a moment.'); return; }
    setLoading(true); setError('');

    const patientData = {
      full_name: fullName.trim(),
      dob: dobString,
      mother_name: motherName.trim(),
      patient_id: patientId,
      raw_id: rawId,
    };
    setPatient(patientData);

    const payload = { full_name: fullName.trim(), dob: dobString, patient_id: patientId };

    try {
      const res = await fetch(`${API_URL}/add-patient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) setSuccess(patientData);
      else setError(data.message || 'Failed to save patient.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <Layout>
        <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center', padding: '32px 0' }}>
          <div style={{ width: 68, height: 68, borderRadius: '50%', background: 'var(--green-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <CheckCircle size={34} color="var(--green)" />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)', fontFamily: "'DM Serif Display', serif", marginBottom: 6 }}>Patient Registered</h2>
          <p style={{ color: 'var(--gray-500)', marginBottom: 24, fontSize: 14 }}>Patient information has been saved successfully.</p>

          <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid var(--gray-200)', boxShadow: 'var(--shadow-sm)', marginBottom: 24, textAlign: 'left' }}>
            {[
              ['Full Name', success.full_name],
              ['Date of Birth', success.dob],
              ["Mother's Name", success.mother_name],
              ['Raw Key', success.raw_id],
              ['Patient ID', success.patient_id],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--gray-100)' }}>
                <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>{l}</span>
                <span style={{
                  fontSize: 13, color: 'var(--navy)', fontWeight: 600,
                  fontFamily: (l === 'Patient ID' || l === 'Raw Key') ? "'JetBrains Mono', monospace" : 'inherit',
                  wordBreak: 'break-all', textAlign: 'right', maxWidth: '60%'
                }}>{v}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Btn variant="secondary" onClick={() => { setSuccess(null); setForm({ fullName: '', motherName: '' }); setDob({ month: '', day: '', year: '' }); }}>Add Another</Btn>
            <Btn onClick={() => navigate('/analysis')}>Proceed to Analysis →</Btn>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader title="New Patient" subtitle="Register a patient before beginning urinalysis." />
      <div style={{ maxWidth: 580, margin: '0 auto' }}>
        <Section>
          {error && <div style={{ marginBottom: 18 }}><Alert type="error">{error}</Alert></div>}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Full Name */}
            <TextInput
              label="Full Name" icon={User} value={form.fullName}
              onChange={e => set('fullName', e.target.value.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '))}
              placeholder="e.g. Jack Smith"
            />

            {/* DOB — three selects (replaces input[type=date] which breaks on iOS Safari) */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Calendar size={14} />
                Date of Birth
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={dob.month}
                  onChange={e => setDob(d => ({ ...d, month: e.target.value }))}
                  style={{ flex: '2 1 0', minWidth: 0, ...selectStyle }}
                >
                  <option value="">Month</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                  ))}
                </select>
                <select
                  value={dob.day}
                  onChange={e => setDob(d => ({ ...d, day: e.target.value }))}
                  style={{ flex: '1 1 0', minWidth: 0, ...selectStyle }}
                >
                  <option value="">Day</option>
                  {DAYS.map(d => (
                    <option key={d} value={d}>{parseInt(d)}</option>
                  ))}
                </select>
                <select
                  value={dob.year}
                  onChange={e => setDob(d => ({ ...d, year: e.target.value }))}
                  style={{ flex: '2 1 0', minWidth: 0, ...selectStyle }}
                >
                  <option value="">Year</option>
                  {YEARS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mother's Name */}
            <TextInput
              label="Mother's First & Last Name" icon={Heart} value={form.motherName}
              onChange={e => set('motherName', e.target.value.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '))}
              placeholder="e.g. Jolly King"
            />

            {/* Live ID preview */}
            {patientId && (
              <div style={{ background: 'var(--blue-pale)', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(30,111,255,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <Hash size={16} color="var(--blue)" style={{ flexShrink: 0 }} />
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Auto-generated Patient ID</p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <div>
                    <p style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 2 }}>Raw key</p>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', fontFamily: "'JetBrains Mono', monospace" }}>{rawId}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 2 }}>Patient ID</p>
                    <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', fontFamily: "'JetBrains Mono', monospace" }}>{patientId}</p>
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '12px 16px', border: '1px solid var(--gray-200)' }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.6 }}>
                <strong>Note:</strong> Patient ID is a SHA-256 hash of <em>INITIALS-DOB-MOTHER_INITIALS</em> (e.g. <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>JS-1980-02-20-JK</code>). Ensure all information is accurate before submission.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, paddingTop: 2 }}>
              <Btn variant="secondary" type="button" onClick={() => navigate('/')} style={{ flex: 1 }}>Cancel</Btn>
              <Btn type="submit" loading={loading} icon={UserPlus} style={{ flex: 2 }}>Register Patient</Btn>
            </div>
          </form>
        </Section>
      </div>
    </Layout>
  );
}

// Matches your app's existing input styling
const selectStyle = {
  height: 42,
  border: '1.5px solid var(--gray-200)',
  borderRadius: 10,
  padding: '0 10px',
  fontSize: 14,
  color: 'var(--navy)',
  background: '#fff',
  appearance: 'auto',
  boxSizing: 'border-box',
  width: '100%',
};

